'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { BrandForm, type BrandFormRef } from '@/components/partials/brands'
import { FormSkeleton, ContentError } from '@/components/partials'
import { useBrand, useUpdateBrand } from '@/hooks'
import { UpdateBrandParams } from '@/types/brand'
import { FormModeEnum } from '@/enums'

type PropsType = {
  id: string
}

const UpdateBrand = ({ id }: PropsType) => {
  const router = useRouter()
  const t = useTranslations()
  const queryClient = useQueryClient()
  const [isRedirecting, setIsRedirecting] = useState(false)
  const brandFormRef = useRef<BrandFormRef>(null)

  const { data: brandData, isLoading, error } = useBrand(id)
  const { mutate: updateBrand, isPending } = useUpdateBrand()

  const handleSubmit = (data: UpdateBrandParams) => {
    updateBrand(
      { brandId: id, params: data },
      {
        onSuccess: async updatedBrand => {
          setIsRedirecting(true)
          queryClient.setQueryData(['brand', id], updatedBrand)
          await queryClient.invalidateQueries({ queryKey: ['brands'] })
          toast.success(t('updated_successfully'))
          router.push(`/products/brands/${id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (brandFormRef.current) {
        brandFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_updating'))
  }

  const handleCancel = () => {
    router.push(`/products/brands/${id}`)
  }

  if (isLoading) {
    return <FormSkeleton fieldRows={1} showPictures />
  }

  if (error || !brandData) {
    return <ContentError error={error} listUrl="/products/brands" />
  }

  return (
    <BrandForm
      ref={brandFormRef}
      mode={FormModeEnum.UPDATE}
      initialData={brandData}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default UpdateBrand
